/**
 * Turns a structured audit row into something an administrator can read.
 *
 * The database model is good and is left alone: actor, action, target, success
 * and a JSONB metadata blob. What was missing was a presentation layer, so the
 * dashboard rendered `JSON.stringify(metadata)` and an administrator reviewing
 * an incident had to parse JSON by eye.
 *
 * Every renderer here is written against metadata shapes taken from the real
 * audit table, not invented. An action with no renderer degrades to a readable
 * title plus labelled key/value pairs rather than raw JSON, so a new event type
 * added on the backend never regresses this page.
 */

export type Severity = 'info' | 'warning' | 'high' | 'critical';

export interface AuditRow {
  id: number | string;
  action: string;
  actor_type?: string | null;
  actor_label?: string | null;
  target_type?: string | null;
  target_label?: string | null;
  success: boolean;
  severity?: Severity | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
}

export interface PresentedField {
  label: string;
  value: string;
}

export interface PresentedEvent {
  /** Short, upper-case headline, e.g. "POLICY APPLIED". */
  title: string;
  /** One sentence of what happened, in plain words. */
  summary: string;
  /** Labelled detail rows, already formatted. */
  fields: PresentedField[];
  severity: Severity;
}

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v : null;
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
}

function formatWhen(iso: unknown): string | null {
  const s = str(iso);
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString();
}

/** Human title for an action, used by the renderer and by the fallback. */
const TITLES: Record<string, string> = {
  'admin.login': 'ADMIN SIGNED IN',
  'admin.login_failed': 'SIGN-IN FAILED',
  'admin.logout': 'ADMIN SIGNED OUT',
  'enrollment.token_generated': 'ENROLLMENT TOKEN CREATED',
  'enrollment.token_revoked': 'ENROLLMENT TOKEN REVOKED',
  'device.enrolled': 'DEVICE ENROLLED',
  'device.enrollment_failed': 'ENROLLMENT FAILED',
  'device.policy_changed': 'DEVICE POLICY CHANGED',
  'device.rebooted': 'DEVICE REBOOTED',
  'device.checkin_delayed': 'CHECK-IN DELAYED',
  'device.offline': 'DEVICE OFFLINE',
  'device.reconnected': 'DEVICE RECONNECTED',
  'device.reenrolled': 'DEVICE RE-ENROLLED',
  'device.possible_factory_reset': 'POSSIBLE FACTORY RESET',
  'device.identity_changed': 'DEVICE IDENTITY CHANGED',
  'policy.updated': 'POLICY UPDATED',
  'policy.rejected_unsafe': 'POLICY REJECTED AS UNSAFE',
  'policy.applied': 'POLICY APPLIED',
  'student.assignment_created': 'STUDENT ASSIGNED',
  'student.assignment_released': 'ASSIGNMENT RELEASED',
  'student.assignment_changed': 'STUDENT REASSIGNED',
  'student.assignment_refused': 'REASSIGNMENT REFUSED',
  'policy.partially_applied': 'POLICY PARTIALLY APPLIED',
  'apk.uploaded': 'APPLICATION UPLOADED',
  'apk.upload_rejected': 'APPLICATION UPLOAD REJECTED',
  'deployment.started': 'DEPLOYMENT STARTED',
  'application.installed': 'APPLICATION INSTALLED',
  'application.install_failed': 'APPLICATION INSTALL FAILED',
  'kiosk.enabled': 'KIOSK ENABLED',
  'kiosk.disabled': 'KIOSK DISABLED',

  // --- Destructive and security-control events --------------------------
  'device.wipe_requested': 'REMOTE WIPE REQUESTED',
  'device.wipe_refused': 'REMOTE WIPE REFUSED',
  'device.wiped': 'DEVICE WIPED',
  'device.locked': 'DEVICE LOCKED',
  'device.kiosk_started': 'KIOSK START REQUESTED',
  'device.kiosk_stopped': 'KIOSK STOP REQUESTED',
  'policy.delivered': 'POLICY DELIVERED TO DEVICE',
  'policy.failed': 'POLICY FAILED',
  'policy.validated': 'POLICY MARKED VALIDATED',
  'application.uninstall_requested': 'APPLICATION UNINSTALL REQUESTED',
  'application.data_cleared': 'APPLICATION DATA CLEARED',
  'application.signature_mismatch': 'APPLICATION SIGNATURE MISMATCH',
  'security.kiosk_control': 'KIOSK CONTROL RESULT',
  'security.camera_restriction': 'CAMERA RESTRICTION RESULT',
  'security.screen_capture_restriction': 'SCREEN CAPTURE RESTRICTION RESULT',
  'security.usb_restriction': 'USB RESTRICTION RESULT',
  'security.debugging_restriction': 'USB DEBUGGING RESTRICTION RESULT',
  'security.factory_reset_restriction': 'FACTORY RESET RESTRICTION RESULT',
  'security.file_manager_restriction': 'FILE MANAGER RESTRICTION RESULT',
  'security.failed_password_wipe_threshold': 'FAILED-PASSWORD WIPE THRESHOLD RESULT',
  'security.frp': 'FACTORY RESET PROTECTION RESULT',
};

/** Title-cases an unknown action so the fallback still reads as English. */
function fallbackTitle(action: string): string {
  return action.replace(/[._]/g, ' ').toUpperCase();
}

/** Turns an unknown metadata object into labelled rows instead of raw JSON. */
function fallbackFields(metadata: Record<string, unknown>): PresentedField[] {
  return Object.entries(metadata)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .slice(0, 8)
    .map(([key, value]) => ({
      // camelCase / snake_case -> "Camel case"
      label: key
        .replace(/[_.]/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/^./, (c) => c.toUpperCase()),
      value:
        typeof value === 'object'
          ? Array.isArray(value)
            ? `${value.length} item(s)`
            : '(details)'
          : String(value),
    }));
}

/**
 * Severity for rows written before the severity column existed, and for
 * backends that do not set it. A failed security-relevant action is never
 * rendered as routine.
 */
function inferSeverity(row: AuditRow): Severity {
  if (row.severity) return row.severity;
  if (row.action === 'device.possible_factory_reset') return 'critical';
  if (row.action === 'device.identity_changed') return 'high';
  if (!row.success) {
    return row.action.startsWith('admin.') || row.action.startsWith('policy.')
      ? 'high'
      : 'warning';
  }
  if (row.action === 'device.offline' || row.action === 'device.checkin_delayed') {
    return 'warning';
  }
  return 'info';
}

export function presentAuditEvent(row: AuditRow): PresentedEvent {
  const m = row.metadata ?? {};
  const severity = inferSeverity(row);
  const title = TITLES[row.action] ?? fallbackTitle(row.action);
  const device = str(row.target_label);
  const fields: PresentedField[] = [];

  switch (row.action) {
    /**
     * Student assignment.
     *
     * Rendered as previous-and-new rather than a single "student" field: the
     * question an audit reader has about a reassignment is who LOST the device
     * as much as who gained it, and one field can only answer half of that.
     */
    case 'student.assignment_created':
    case 'student.assignment_released':
    case 'student.assignment_changed':
    case 'student.assignment_refused': {
      const studentName = str(m.studentName);
      const studentRef = str(m.studentRef);
      const previousName = str(m.previousStudentName);
      const previousRef = str(m.previousStudentRef);
      const reason = str(m.reason);

      if (device) fields.push({ label: 'Device', value: device });
      if (previousName) {
        fields.push({
          label: 'Previous student',
          value: previousRef ? `${previousName} (${previousRef})` : previousName,
        });
      }
      if (studentName) {
        fields.push({
          label: row.action === 'student.assignment_changed' ? 'New student' : 'Student',
          value: studentRef ? `${studentName} (${studentRef})` : studentName,
        });
      }
      if (m.via) fields.push({ label: 'Assigned by', value: String(m.via) });
      if (reason) fields.push({ label: 'Reason', value: reason });

      const summary =
        row.action === 'student.assignment_released'
          ? `${previousName ?? 'A student'} was released from ${device ?? 'this device'}. ` +
            'It can now be assigned to someone else through the enrollment workflow.'
          : row.action === 'student.assignment_changed'
            ? `${device ?? 'This device'} moved from ${previousName ?? 'a previous student'} ` +
              `to ${studentName ?? 'a new student'}.`
            : row.action === 'student.assignment_refused'
              ? 'A device tried to assign itself to a different student and was refused. ' +
                'Only an administrator can change an assignment.'
              : `${device ?? 'A device'} was assigned to ${studentName ?? 'a student'}.`;

      return { title, severity, summary, fields };
    }

    case 'device.enrolled': {
      const model = str(m.model);
      const version = str(m.androidVersion);
      const sdk = num(m.androidSdkInt);
      if (device) fields.push({ label: 'Device', value: device });
      if (model) fields.push({ label: 'Model', value: model });
      if (version) {
        fields.push({
          label: 'Android',
          value: sdk !== null ? `${version} (API ${sdk})` : version,
        });
      }
      fields.push({
        label: 'Device Owner',
        value: m.isDeviceOwner === true ? 'Yes' : 'No — policy cannot be enforced',
      });
      // Shown when present. A device enrolled without a student name - which is
      // what an older agent does - simply omits the row.
      const enrolledStudent = str(m.studentName);
      const enrolledRef = str(m.studentRef);
      if (enrolledStudent) {
        fields.push({
          label: 'Student',
          value: enrolledRef ? `${enrolledStudent} (${enrolledRef})` : enrolledStudent,
        });
      }
      return {
        title,
        severity: m.isDeviceOwner === true ? severity : 'high',
        summary:
          m.isDeviceOwner === true
            ? `${device ?? 'A device'} enrolled as a fully managed device.`
            : `${device ?? 'A device'} enrolled but is NOT Device Owner, so policy cannot be enforced.`,
        fields,
      };
    }

    case 'enrollment.token_generated': {
      const expires = formatWhen(m.expiresAt);
      if (expires) fields.push({ label: 'Expires', value: expires });
      fields.push({
        label: 'Reusable',
        value: m.oneTimeOnly === false ? 'Yes' : 'No — single use',
      });
      return {
        title,
        severity,
        summary: 'An enrollment token was generated. The token itself is never stored or logged.',
        fields,
      };
    }

    case 'apk.uploaded': {
      const pkg = str(m.packageName);
      const size = num(m.sizeBytes);
      const sha = str(m.sha256);
      if (pkg) fields.push({ label: 'Package', value: pkg });
      if (num(m.versionCode) !== null) {
        fields.push({ label: 'Version code', value: String(m.versionCode) });
      }
      if (size !== null) fields.push({ label: 'Size', value: formatBytes(size) });
      if (sha) fields.push({ label: 'SHA-256', value: `${sha.slice(0, 16)}…` });
      return {
        title,
        severity,
        summary: `${pkg ?? 'An application'} was uploaded and its signature verified.`,
        fields,
      };
    }

    case 'apk.upload_rejected': {
      const reason = str(m.reason) ?? str(m.message);
      if (reason) fields.push({ label: 'Reason', value: reason });
      return {
        title,
        severity: 'warning',
        summary: 'An uploaded APK failed validation and was not stored.',
        fields: fields.length ? fields : fallbackFields(m),
      };
    }

    case 'deployment.started': {
      const pkg = str(m.packageName);
      const targets = num(m.targetCount);
      const online = num(m.onlineCount);
      const offline = num(m.offlineCount);
      if (pkg) fields.push({ label: 'Application', value: pkg });
      if (targets !== null) fields.push({ label: 'Devices', value: String(targets) });
      if (online !== null && offline !== null) {
        fields.push({
          label: 'Reachable now',
          value: `${online} online, ${offline} offline (will install on next check-in)`,
        });
      }
      return {
        title,
        severity,
        summary: `Deployment of ${pkg ?? 'an application'} started.`,
        fields: fields.length ? fields : fallbackFields(m),
      };
    }

    case 'device.possible_factory_reset':
      return {
        title,
        severity: 'critical',
        summary:
          `${device ?? 'A device'} went silent and then re-enrolled with a new agent ` +
          'identity. That is consistent with a factory reset, including a recovery-mode ' +
          'wipe — but it is not proof; a re-provisioned or repaired device looks the same.',
        fields: fallbackFields(m),
      };

    case 'device.offline':
      return {
        title,
        severity: 'warning',
        summary:
          `${device ?? 'A device'} stopped checking in. It may be powered off, off the ` +
          'network, or wiped — this signal alone cannot tell them apart.',
        fields: fallbackFields(m),
      };

    case 'device.checkin_delayed':
      return {
        title,
        severity: 'warning',
        summary:
          `${device ?? 'A device'} is checking in later than expected. This is often OEM ` +
          'battery management rather than a missing device.',
        fields: fallbackFields(m),
      };

    case 'policy.rejected_unsafe': {
      const reason = str(m.reason) ?? str(m.message);
      if (reason) fields.push({ label: 'Reason', value: reason });
      return {
        title,
        severity: 'high',
        summary: 'A policy was refused because applying it could strand devices offline.',
        fields: fields.length ? fields : fallbackFields(m),
      };
    }

    case 'admin.login_failed':
      return {
        title,
        severity: 'high',
        summary: `A sign-in attempt failed for ${row.actor_label ?? 'an account'}.`,
        fields: fallbackFields(m),
      };

    default:
      return {
        title,
        severity,
        summary: row.success
          ? `${title.toLowerCase().replace(/^./, (c) => c.toUpperCase())}.`
          : 'The action did not complete.',
        fields: fallbackFields(m),
      };
  }
}

export const SEVERITY_ORDER: Severity[] = ['info', 'warning', 'high', 'critical'];
